/**
 * The recursive org-chart table — shared by the Overview tab (capped at `maxDepth={1}` and
 * `flat`, so it renders FINN plus one flush, non-indented row per department, each a plain jump to
 * that department's own tab rather than something to expand) and each department tab
 * (`maxDepth={Infinity}`, `flat` off, rooted at that department's own node instead of FINN, so it
 * behaves exactly like the pre-split single table, complete with its own collapse/expand chevrons).
 * See `InternalPlaygroundHiringTimelineView` for how the two modes are wired up, and
 * `proxy-server/lib/internalPlaygroundTimeline.js`'s module doc for the full reasoning behind the
 * tree shape itself (sub-managers vs. Role-grouped leaves, the named exceptions to Personio's
 * literal reporting lines, etc.).
 */
import { Fragment } from 'react';
import { ChevronDown, ChevronRight, User, UserPlus } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Badge } from '@finn-auto/design-system/atoms/badge';
import { TRIMESTER_NUMBER_BY_END_MONTH } from '../taCockpit/trimester';
import type { OrgLeaf, OrgNode } from '../../services/internalPlaygroundApi';
import { todayIso } from './timelineFilters';
import { BackfillControl, type BackfillPerson } from './BackfillControl';
import { EditRoleButton, type RoleEditing } from './EditRoleButton';

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatMonthLabel(month: string): string {
  const [year, mm] = month.split('-');
  return `${MONTH_ABBR[Number(mm) - 1]} '${year.slice(2)}`;
}

/** 'YYYY-MM-DD' → '5 Jan 2027', for the leaver/incoming-hire annotation below — the month-column
 * header's own `formatMonthLabel` only ever has a month to work with, never a day. */
function formatFullDate(date: string): string {
  const [year, mm, dd] = date.split('-');
  return `${Number(dd)} ${MONTH_ABBR[Number(mm) - 1]} ${year}`;
}

/**
 * The known-leaver / known-incoming-hire annotation shown after a person's name (David, Sep 2026).
 * Termination wins over hire when a record somehow carries both — a person who is leaving is not
 * also "joining". Only ever rendered for a REAL person (`hireDate`/`terminationDate` are never set
 * on an open/planned role's `OrgLeaf`, or on the synthetic `Unassigned` node).
 */
function PersonDateNote({
  hireDate,
  terminationDate,
}: {
  hireDate?: string | null;
  terminationDate?: string | null;
}) {
  if (terminationDate) {
    const isFuture = terminationDate > todayIso();
    return (
      <span className="truncate text-xs italic text-red">
        - {isFuture ? 'contract ends on' : 'contract ended on'} {formatFullDate(terminationDate)}
      </span>
    );
  }
  if (hireDate && hireDate > todayIso()) {
    return <span className="truncate text-xs italic text-orange">- joins {formatFullDate(hireDate)}</span>;
  }
  return null;
}

export interface MonthGroup {
  /** The group's last month — stable, unique, and what a collapsed group shows on its own. */
  key: string;
  label: string;
  months: string[];
}

/** Chunk the (already Jan→Dec, year-ordered) month list into trimester groups of 4, Google-Sheets-
 * column-grouping style: each group can be collapsed to just its last month or expanded to all 4. */
export function buildMonthGroups(months: string[]): MonthGroup[] {
  const groups: MonthGroup[] = [];
  for (let i = 0; i < months.length; i += 4) {
    const groupMonths = months.slice(i, i + 4);
    const last = groupMonths[groupMonths.length - 1];
    const [year, mm] = last.split('-');
    groups.push({ key: last, label: `${TRIMESTER_NUMBER_BY_END_MONTH[mm] ?? mm} '${year.slice(2)}`, months: groupMonths });
  }
  return groups;
}

function formatFte(value: number): string {
  if (!value) return '–';
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

/**
 * A node's FTE cell. Three figures, each with exactly ONE meaning, which is the whole design
 * (David, Sep 2026):
 *
 *   - BLACK is committed FTE — real employees plus planned/approved roles, under the current
 *     filter. Never includes proposed roles.
 *   - PURPLE `+N` is the proposed delta for this subtree, matching the purple rows it comes from.
 *     Absent when nothing here is proposed, so a tree with no proposals renders exactly as it did
 *     before proposals existed.
 *   - GREY underneath is the same cell UNFILTERED, shown only while a filter is narrowing.
 *
 * The rejected alternative was black = incl. proposed with grey = excl.: it reads well until a
 * filter is also active, at which point grey has to mean both "the bigger number you are hiding"
 * and "the smaller committed one", and one of the two comparisons has to be dropped.
 *
 * Never rendered for a leaf: a leaf's own `fteByMonth` is either shown in full or not at all
 * (filtering excludes a leaf outright, it never shrinks one — see `fteByMonthByNodeId`'s doc), so
 * there is nothing to compare there. The unfiltered figure is skipped when it's zero too — an
 * always-empty cell (no headcount there at all, independent of any filter) showing "–" twice would
 * be noise on every quiet cell of an otherwise sparse two-year table.
 */
function FteCell({
  value,
  proposedValue = 0,
  unfilteredValue,
}: {
  value: number;
  proposedValue?: number;
  unfilteredValue?: number;
}) {
  // Also skipped when it EQUALS the filtered figure: unticking only "Proposed" narrows the tree
  // without changing committed FTE at all, so without this every populated cell in a 24-column
  // table would print the same number twice. The grey line exists to show a difference.
  const showUnfiltered = unfilteredValue !== undefined && unfilteredValue > 0 && unfilteredValue !== value;
  return (
    <td className="px-3 py-2 text-right text-sm font-medium tabular-nums text-zinc-700">
      <div>
        {formatFte(value)}
        {proposedValue > 0 && (
          <span className="ml-1 text-xs font-normal text-purple-600">+{formatFte(proposedValue)}</span>
        )}
      </div>
      {showUnfiltered && <div className="text-xs font-normal tabular-nums text-zinc-400">{formatFte(unfilteredValue)}</div>}
    </td>
  );
}

/** Depth 0 (the table's own root) and depth 1 start expanded; everything deeper starts collapsed.
 * A pure function of depth, never of what's currently loaded, so a node that appears later (e.g. a
 * refetch adding a department) still gets the right default instead of silently rendering
 * collapsed. Depth is always relative to whatever `root` a given `OrgChartTable` was given — a
 * department re-rooted for its own tab is depth 0 there, same as FINN is on Overview. */
function defaultExpanded(depth: number): boolean {
  return depth <= 1;
}

const DEPTH_LABEL_CLASS = ['font-bold text-zinc-900', 'font-semibold text-zinc-900', 'font-medium text-zinc-800'];

/** A node's own label. A plain node (no `groupLabel`) shows just its name, weighted by depth. A
 * department/unit node shows its team/department name in bold black next to the person heading it
 * in FINN's own CI blue — replacing the earlier plain "Label | Name" text (David, Sep 2026, matching
 * the reference screenshot). */
function NodeLabel({ node, depthClassIndex }: { node: OrgNode; depthClassIndex: number }) {
  if (!node.groupLabel) {
    return (
      <span className="inline-flex min-w-0 items-baseline gap-1.5 truncate text-sm">
        <span className={`truncate ${DEPTH_LABEL_CLASS[depthClassIndex]}`}>{node.personName}</span>
        <PersonDateNote hireDate={node.hireDate} terminationDate={node.terminationDate} />
      </span>
    );
  }
  return (
    <span className="inline-flex min-w-0 items-baseline gap-1.5 truncate text-sm">
      {/* Bold black at every depth, deliberately not `DEPTH_LABEL_CLASS` — the reference screenshot
       * shows a nested unit label ("Sales" under "Growth") at the same weight as its parent's own
       * department label, not lighter (CodeRabbit, PR #370). */}
      <span className="truncate font-bold text-zinc-900">{node.groupLabel}</span>
      <span className="truncate font-medium text-trustedBlue">{node.personName}</span>
      <PersonDateNote hireDate={node.hireDate} terminationDate={node.terminationDate} />
    </span>
  );
}

/**
 * ⚠️ A hand-rolled pill rather than the design-system `Badge`, and deliberately so: `Badge` ships
 * `monochrome | blue | red | orange | green | grey | monochromeBlue` and there is no purple among
 * them. Purple is not decoration here — it is what ties this row to the purple `+N` in every
 * ancestor's FTE cell, so substituting an available variant would break the one thing the colour
 * is doing. Same documented deviation, for the same reason, as `RatingBadge` in Role
 * Prioritisation (the design system ships no ordinal ramp either). Sized to match the `Badge`
 * calls beside it so the two read as one row.
 */
function ProposedBadge() {
  return (
    <span className="inline-flex shrink-0 items-center rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium text-purple-700">
      proposed
    </span>
  );
}

/**
 * A leaf row. A `proposed` leaf is purple throughout — icon, label, badge and its own FTE figures
 * — so it reads as one thing rather than an ordinary row wearing a tag, and so it ties visually
 * to the purple `+N` its FTE lands in on every ancestor's cell (see `FteCell`).
 *
 * An APPROVED proposal is deliberately NOT purple: it renders as an ordinary planned role, because
 * approval is the point at which it stops being speculative. The proxy has already collapsed that
 * distinction into `kind` (`demandRolesFromProposals`), so there is nothing to decide here.
 *
 * `proposalRational` is the proposer's free text, shown as a native tooltip rather than inline —
 * it is a sentence or a paragraph, and the row is one line in a table that is already scrolling
 * horizontally across 24 month columns.
 */
function OrgLeafRow({ leaf, depth, columns, onBackfillOpen, edit }: {
  leaf: OrgLeaf; depth: number; columns: string[]; onBackfillOpen?: (person: BackfillPerson) => void;
  edit?: RoleEditing;
}) {
  const proposed = leaf.kind === 'proposed';
  return (
    <tr className="border-b border-zinc-50">
      <td className="sticky left-0 bg-white py-1.5 pr-4" style={{ paddingLeft: `${depth * 20 + 12}px` }}>
        <span className={`inline-flex items-center gap-1.5 text-sm ${proposed ? 'text-purple-700' : 'text-zinc-500'}`}>
          <span className="inline-block h-3.5 w-3.5 shrink-0" />
          {leaf.kind === 'actual' ? (
            <User className="h-3.5 w-3.5 shrink-0 text-zinc-300" />
          ) : (
            <UserPlus className={`h-3.5 w-3.5 shrink-0 ${proposed ? 'text-purple-500' : 'text-amber-500'}`} />
          )}
          <span className="truncate" title={proposed ? leaf.proposalRational || undefined : undefined}>
            {leaf.label}
          </span>
          {leaf.kind === 'actual' && (
            <PersonDateNote hireDate={leaf.hireDate} terminationDate={leaf.terminationDate} />
          )}
          <BackfillControl employeeId={leaf.id.replace(/^person__/, '')} name={leaf.label}
            terminationDate={leaf.terminationDate} backfill={leaf.backfill} onOpen={onBackfillOpen} />
          {proposed && <ProposedBadge />}
          {leaf.kind === 'planned' && leaf.status && (
            <Badge variant="orange" className="shrink-0 !px-1.5 !py-0.5 !text-[10px]">
              {leaf.status}
            </Badge>
          )}
          {leaf.undated && (
            <Badge variant="red" className="shrink-0 !px-1.5 !py-0.5 !text-[10px]">
              no date
            </Badge>
          )}
          <EditRoleButton leaf={leaf} edit={edit} />
        </span>
      </td>
      {columns.map((month) => (
        <td
          key={month}
          className={`px-3 py-1.5 text-right text-sm tabular-nums ${proposed ? 'text-purple-600' : 'text-zinc-500'}`}
        >
          {formatFte(leaf.fteByMonth[month] || 0)}
        </td>
      ))}
    </tr>
  );
}

/** A non-collapsible sort header — Role is "a sorting mechanism, it does not need to be
 * collapsible" (David). Always rendered when its parent is expanded; carries no FTE of its own. */
function RoleHeaderRow({ role, depth, columnCount }: { role: string; depth: number; columnCount: number }) {
  return (
    <tr>
      <td
        className="sticky left-0 bg-white pb-0.5 pt-2 text-xs italic text-zinc-400"
        style={{ paddingLeft: `${depth * 20 + 12}px` }}
      >
        {role}
      </td>
      <td colSpan={columnCount} className="bg-white" />
    </tr>
  );
}

function OrgNodeRow({
  node,
  depth,
  maxDepth,
  columns,
  toggledIds,
  onToggle,
  onDeadEndClick,
  flat = false,
  unfilteredFteById,
  onBackfillOpen,
  edit,
}: {
  node: OrgNode;
  depth: number;
  maxDepth: number;
  columns: string[];
  toggledIds: Set<string>;
  onToggle: (id: string) => void;
  onDeadEndClick?: (node: OrgNode) => void;
  /** Overview mode (`maxDepth={1}`): every row jumps to a tab rather than expanding, so there's
   * nothing left to collapse/expand and no reason for a department row to sit deeper than the
   * FINN row above it. Suppresses the chevron and forces every row's indent to depth 0, while
   * `depth` itself still drives the real `maxDepth` cap and `defaultExpanded` above. */
  flat?: boolean;
  /** Every node id's UNFILTERED `fteByMonth`, present only while a filter is actually narrowing
   * the tree — see `FteCell`'s doc and `isFilteringActive` in `timelineFilters.ts`. Absent (rather
   * than `undefined`-valued but present) is what turns the whole grey row off, not a boolean flag,
   * so `OrgChartTable`'s caller has exactly one thing to decide. */
  unfilteredFteById?: Map<string, Record<string, number>>;
  onBackfillOpen?: (person: BackfillPerson) => void;
  edit?: RoleEditing;
}) {
  // A node whose depth has hit the cap never expands here, however much it actually has beneath
  // it — that's the whole difference between the Overview table (maxDepth=1) and a department
  // table (maxDepth=Infinity, so this never applies). `onDeadEndClick` is what lets a capped row
  // still be useful: clicking it navigates to that department's own tab instead of toggling.
  const cappedHere = depth >= maxDepth;
  const hasContent = !cappedHere && (node.subManagers.length > 0 || node.roleGroups.length > 0);
  const isExpanded = toggledIds.has(node.id) ? !defaultExpanded(depth) : defaultExpanded(depth);
  const isDeadEndLink = cappedHere && Boolean(onDeadEndClick);
  const showChevron = !flat && (hasContent || isDeadEndLink);
  const indentDepth = flat ? 0 : depth;
  const label = <NodeLabel node={node} depthClassIndex={Math.min(indentDepth, 2)} />;

  return (
    <>
      <tr className="border-b border-zinc-100 hover:bg-zinc-50">
        <td className="sticky left-0 bg-white py-2 pr-4" style={{ paddingLeft: `${indentDepth * 20 + 12}px` }}>
          <div className="flex items-center gap-1.5">
            {flat && !isDeadEndLink ? (
              <span className="inline-flex py-0.5">{label}</span>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-expanded={!flat && hasContent ? isExpanded : undefined}
                onClick={() => {
                  if (flat) {
                    if (isDeadEndLink) onDeadEndClick?.(node);
                    return;
                  }
                  if (hasContent) onToggle(node.id);
                  else if (cappedHere) onDeadEndClick?.(node);
                }}
                disabled={flat ? false : !showChevron}
                className="!h-auto !min-h-0 justify-start gap-1.5 !px-1 !py-0.5 text-left"
              >
                {showChevron && (
                  hasContent && isExpanded ? (
                    <ChevronDown className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
                  )
                )}
                {!showChevron && !flat && <span className="inline-block h-3.5 w-3.5 shrink-0" />}
                {label}
              </Button>
            )}
            {/* Beside the toggle, never inside it: the label is in a <button>, and nesting is invalid. */}
            <BackfillControl employeeId={node.id} name={node.personName} terminationDate={node.terminationDate}
              backfill={node.backfill} onOpen={onBackfillOpen} />
          </div>
        </td>
        {columns.map((month) => (
          <FteCell
            key={month}
            value={node.fteByMonth[month] || 0}
            proposedValue={node.proposedFteByMonth?.[month] || 0}
            unfilteredValue={unfilteredFteById ? unfilteredFteById.get(node.id)?.[month] || 0 : undefined}
          />
        ))}
      </tr>
      {hasContent &&
        isExpanded &&
        node.subManagers.map((child) => (
          <OrgNodeRow
            key={child.id}
            node={child}
            depth={depth + 1}
            maxDepth={maxDepth}
            columns={columns}
            toggledIds={toggledIds}
            onToggle={onToggle}
            onDeadEndClick={onDeadEndClick}
            flat={flat}
            unfilteredFteById={unfilteredFteById}
            onBackfillOpen={onBackfillOpen}
            edit={edit}
          />
        ))}
      {hasContent &&
        isExpanded &&
        node.roleGroups.map((group) => (
          <Fragment key={group.role}>
            <RoleHeaderRow role={group.role} depth={depth + 1} columnCount={columns.length} />
            {group.entries.map((leaf) => (
              <OrgLeafRow key={leaf.id} leaf={leaf} depth={depth + 1} columns={columns} onBackfillOpen={onBackfillOpen} edit={edit} />
            ))}
          </Fragment>
        ))}
    </>
  );
}

export function OrgChartTable({
  root,
  columns,
  groups,
  expandedGroupKeys,
  onToggleGroup,
  toggledIds,
  onToggle,
  maxDepth = Infinity,
  onDeadEndClick,
  flat = false,
  unfilteredFteById,
  onBackfillOpen,
  edit,
}: {
  root: OrgNode;
  columns: string[];
  groups: MonthGroup[];
  expandedGroupKeys: Set<string>;
  onToggleGroup: (key: string) => void;
  toggledIds: Set<string>;
  onToggle: (id: string) => void;
  maxDepth?: number;
  onDeadEndClick?: (node: OrgNode) => void;
  /** Overview mode: no collapse/expand affordance anywhere, and every row shares the root's own
   * indent — see `OrgNodeRow`'s own `flat` doc. */
  flat?: boolean;
  /** See `OrgNodeRow`'s own doc — omit entirely when no filter is narrowing the tree. */
  unfilteredFteById?: Map<string, Record<string, number>>;
  /** Opens the Manage backfill dialog. Omit to render no backfill controls at all. */
  onBackfillOpen?: (person: BackfillPerson) => void;
  /** Offers "Edit" on planned and proposed rows. Omit to render no edit controls at all. */
  edit?: RoleEditing;
}) {
  return (
    <div className="overflow-x-auto rounded-md border border-zinc-200 bg-white">
      <table className="w-full min-w-max border-collapse">
        <thead>
          <tr className="border-b border-zinc-100 bg-zinc-50">
            <th
              rowSpan={2}
              className="sticky left-0 z-10 bg-zinc-50 px-3 py-2 text-left align-bottom text-xs font-medium uppercase tracking-wide text-zinc-500"
            >
              Org chart
            </th>
            {groups.map((group) => {
              const isExpanded = expandedGroupKeys.has(group.key);
              return (
                <th
                  key={group.key}
                  colSpan={isExpanded ? group.months.length : 1}
                  className="border-l border-zinc-200 px-2 py-1 text-center text-xs font-medium text-zinc-500"
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-expanded={isExpanded}
                    onClick={() => onToggleGroup(group.key)}
                    title={isExpanded ? `Collapse ${group.label}` : `Expand ${group.label}`}
                    className="!h-auto !min-h-0 gap-1 !px-1.5 !py-0.5 !text-xs uppercase tracking-wide"
                  >
                    {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                    {group.label}
                  </Button>
                </th>
              );
            })}
          </tr>
          <tr className="border-b border-zinc-200 bg-zinc-50">
            {columns.map((month) => (
              <th
                key={month}
                className="min-w-[64px] border-l border-zinc-100 px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-zinc-500 first:border-l-zinc-200"
              >
                {formatMonthLabel(month)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <OrgNodeRow
            node={root}
            depth={0}
            maxDepth={maxDepth}
            columns={columns}
            toggledIds={toggledIds}
            onToggle={onToggle}
            onDeadEndClick={onDeadEndClick}
            flat={flat}
            unfilteredFteById={unfilteredFteById}
            onBackfillOpen={onBackfillOpen}
            edit={edit}
          />
        </tbody>
      </table>
    </div>
  );
}
